import React, { useRef, useState, useEffect, useCallback, useMemo } from 'react';
import "../../editorStyleSheet.css"
import PropTypes from "prop-types";
import { getScriptDirection, extractArticleInner, injectArticleInner, extractStyles } from "../../../../utlis/generalUtils";
import CircularProgress from '@mui/material/CircularProgress';
import AudioPlayer from "../wavesurfer/AudioPlayer";
import 'react-edit-text/dist/index.css';
import SoftBox from "../../../../components/SoftBox";
import {IconButton, Tooltip, useMediaQuery, useTheme} from "@mui/material";
import { Undo2, Redo2, Languages, FileText } from "lucide-react";
import { Box, Button, Divider, Typography, ButtonGroup } from "@mui/material"
import DOMPurify from "dompurify";
import { useTranslation } from "react-i18next";
import BlockEditorModal from "./BlockEditorModal";
import { updateChunkContent } from '../../../../axios-client';

const A3_WIDTH_PX = 700;

const DigitizationHtmlBlocksComponent = ({
    pageNumber, configWidth, updateConfigWidth, page, sourceLanguage, targetLanguage, addOrUpdatePages, updatedPages,
    handleSave, translationSelected,  digitizationId, numPages, fetchData, setNotification, setPage,
    hoveredChunkId, setHoveredChunkId, setPdfDimensions
}) => {

    const { t, i18n } = useTranslation(['editor']);
    const dir = i18n.dir();
    const [displayExtractedText, setDisplayExtractedText] = useState(true);
    const [loading, setLoading] = useState(false);
    const [audioUrl, setAudioUrl] = useState('');
    const [scale, setScale] = useState(1);

    const containerRef = useRef(null);
    const iframeRef = useRef(null);

    const theme = useTheme()
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const isRtl = dir === "rtl" ;

    const [isEditing, setIsEditing] = useState(false);
    const [isRegenerating, setIsRegenerating] = useState(false);
    const [contentHeight, setContentHeight] = useState(A3_WIDTH_PX * 1.414);

    // --- click-to-edit state ---
    const [selectedBlock, setSelectedBlock] = useState(null); 
    const [anchorRect, setAnchorRect] = useState(null);
    const [viewerBounds, setViewerBounds] = useState(null); 
    const editedChunksRef = useRef({});
    const originalTextRef = useRef({});
    const [saveError, setSaveError] = useState(null);


    useEffect(() => {

        if (!page) return
        setPdfDimensions({width: page.width_px, height: page.height_px})

    }, [page]);

    useEffect(() => {

        const node = containerRef.current;
        if (node && !configWidth) {
            const nodeScale = Math.min(node.clientWidth / A3_WIDTH_PX, 1);
            setScale(Number(nodeScale.toFixed(3)));
        };

        if (!configWidth) return
        const newScale = Math.min(configWidth / A3_WIDTH_PX, 1);
        setScale(Number(newScale.toFixed(3)));

    }, [configWidth]);


    const handleContentSectionMouseDown = useCallback((e) => {
        e.preventDefault();
        const initialWidth = e.target.parentElement.offsetWidth;
        const initialDivWidth = e.target.offsetParent.offsetWidth;
        const initialX = e.clientX;

        const handleMouseContentSectionMove = (e) => {
            let newWidth = initialWidth - (e.clientX - initialX);

            if (newWidth < initialDivWidth * 0.2) {
                newWidth = (initialDivWidth * 0.2);
            } else if (newWidth > initialDivWidth * 0.75) {
                newWidth = (initialDivWidth * 0.75);
            }

            const contentSection = document.getElementById("digitizationEditorHTMLSection");
            if (contentSection && contentSection.offsetWidth !== newWidth) {
                contentSection.style.width = newWidth + 'px';
                updateConfigWidth(newWidth);
            }
            const event = new CustomEvent('configResized');
            window.dispatchEvent(event);
        };

        const handleMouseContentSectionUp = () => {
            document.removeEventListener('mousemove', handleMouseContentSectionMove);
            document.removeEventListener('mouseup', handleMouseContentSectionUp);
        };

        document.addEventListener('mousemove', handleMouseContentSectionMove);
        document.addEventListener('mouseup', handleMouseContentSectionUp);
    }, [updateConfigWidth]);

    /*
    |--------------------------------------------------------------------------
    | Build complete HTML document
    |--------------------------------------------------------------------------
    */

    const safeHtml = useMemo(() => {
        let html = page?.html || "";

        if (!html) return "";

        html = DOMPurify.sanitize(html, {
            WHOLE_DOCUMENT: true,

            ADD_TAGS: [
                "html",
                "head",
                "body",
                "style",
                "meta",
                "link",
                "figure",
            ],

            ADD_ATTR: [
                "style",
                "class",
                "id",
                "dir",
                "lang",
                "href",
                "rel",
                "src",
                "alt",
                "loading",
                "data-chunk-id",
                "data-type",
            ],
        });

        return html;
    }, [page?.html]);


    /*
    |--------------------------------------------------------------------------
    | Click-to-edit helpers
    |--------------------------------------------------------------------------
    */

    const extractPlainText = (container) => {
        const nodes = Array.from(
            container.querySelectorAll("p, h1, h2, h3, h4, h5, h6")
        );
        if (!nodes.length) return container.innerText.trim();
        return nodes.map((n) => n.innerText.trim()).join("\n\n");
    };

    const applyContentToDom = (chunkId, content) => {
        const doc = iframeRef.current?.contentDocument;
        if (!doc) return;

        const chunkEl = doc.querySelector(
            `[data-chunk-id="${CSS.escape(chunkId)}"]`
        );
        if (!chunkEl) return;

        const nodes = Array.from(
            chunkEl.querySelectorAll("p, h1, h2, h3, h4, h5, h6")
        );
        const paragraphs = content.split(/\n\s*\n/);

        if (nodes.length && nodes.length === paragraphs.length) {
            nodes.forEach((node, i) => {
                node.textContent = paragraphs[i];
            });
        } else if (nodes.length) {
            nodes[0].textContent = content;
            nodes.slice(1).forEach((n) => {
                n.textContent = "";
            });
        }
    };
    /*
    |--------------------------------------------------------------------------
    | Iframe load: text fitting + hover + click handlers
    |--------------------------------------------------------------------------
    */
    const handleLiveChange = (chunkId, content) => {
        applyContentToDom(chunkId, content);
    };

    const handleSaveBlock = (chunkId, content) => {
        const previousContent = editedChunksRef.current[chunkId] ?? originalTextRef.current[chunkId];
        editedChunksRef.current[chunkId] = content;
        applyContentToDom(chunkId, content);
        setSelectedBlock(null);
        setAnchorRect(null);

        updateChunkContent(chunkId, content).catch((error) => {
            console.error(`Failed to save chunk ${chunkId}`, error);
            if (previousContent !== undefined) {
                editedChunksRef.current[chunkId] = previousContent;
                applyContentToDom(chunkId, previousContent);
            }
            setSaveError(chunkId);
        });
    };

    const handleCloseModal = () => {
        setSelectedBlock(null);
        setAnchorRect(null);
    };

    useEffect(() => {
        const iframe = iframeRef.current;

        if (!iframe) return;


        const fitTextInBlocks = (doc) => {
    /*
     * --------------------------------------------------------------------------
     * CONFIGURATION
     * --------------------------------------------------------------------------
     */

    const MIN_FONT_SIZE = 6;
    const MAX_FONT_SIZE = 30;
    const DEFAULT_FONT_SIZE = 14;

    /*
     * Small tolerance for browser sub-pixel calculations.
     */
    const TOLERANCE = 1.5;

    /*
     * Title sizes relative to paragraph size.
     */
    const TITLE_SCALE = {
        h1: 1.65,
        h2: 1.35,
        h3: 1.20,
        h4: 1.10,
        h5: 1.00,
        h6: 0.95,
    };

    /*
     * --------------------------------------------------------------------------
     * BASIC HELPERS
     * --------------------------------------------------------------------------
     */

    const getTag = (element) => {
        return element?.tagName?.toLowerCase() || "";
    };

    const isParagraph = (element) => {
        return getTag(element) === "p";
    };

    const isTitle = (element) => {
        const tag = getTag(element);

        return (
            tag === "h1" ||
            tag === "h2" ||
            tag === "h3" ||
            tag === "h4" ||
            tag === "h5" ||
            tag === "h6"
        );
    };

    const getElementDepth = (element) => {
        let depth = 0;
        let current = element;

        while (
            current &&
            current.parentElement
        ) {
            depth++;
            current = current.parentElement;
        }

        return depth;
    };

    const getDeepestTextContainer = (block) => {
        const containers = Array.from(
            block.querySelectorAll(".text-chunk")
        );

        if (!containers.length) {
            return null;
        }

        const validContainers = containers.filter(
            (container) => {
                return container.querySelector(
                    "p, h1, h2, h3, h4, h5, h6"
                );
            }
        );

        if (!validContainers.length) {
            return null;
        }

        return validContainers.reduce(
            (deepest, current) => {
                return getElementDepth(current) >
                    getElementDepth(deepest)
                    ? current
                    : deepest;
            }
        );
    };

    /*
     * --------------------------------------------------------------------------
     * GET DIRECT TEXT ELEMENTS
     * --------------------------------------------------------------------------
     */

    const getTextElements = (container) => {
        if (!container) {
            return [];
        }

        return Array.from(
            container.children
        ).filter((element) => {
            const tag = getTag(element);

            return (
                tag === "p" ||
                tag === "h1" ||
                tag === "h2" ||
                tag === "h3" ||
                tag === "h4" ||
                tag === "h5" ||
                tag === "h6"
            );
        });
    };

    /*
     * --------------------------------------------------------------------------
     * MARGINALIA
     * --------------------------------------------------------------------------
     */

    const isMarginalia = (block) => {
        return (
            block.classList.contains("marginalia") ||
            block.querySelector(".marginalia") !== null
        );
    };

    /*
     * --------------------------------------------------------------------------
     * GET AVAILABLE BLOCK SIZE
     * --------------------------------------------------------------------------
     */

    const getAvailableSize = (block) => {
        const rect =
            block.getBoundingClientRect();

        const style =
            doc.defaultView.getComputedStyle(
                block
            );

        const paddingLeft =
            parseFloat(style.paddingLeft) || 0;

        const paddingRight =
            parseFloat(style.paddingRight) || 0;

        const paddingTop =
            parseFloat(style.paddingTop) || 0;

        const paddingBottom =
            parseFloat(style.paddingBottom) || 0;

        return {
            width: Math.max(
                0,
                rect.width -
                paddingLeft -
                paddingRight
            ),

            height: Math.max(
                0,
                rect.height -
                paddingTop -
                paddingBottom
            ),
        };
    };

    /*
     * --------------------------------------------------------------------------
     * FORCE LAYOUT
     * --------------------------------------------------------------------------
     */

    const forceLayout = () => {
        void doc.body.offsetHeight;
    };

    /*
     * --------------------------------------------------------------------------
     * APPLY BODY FONT
     * --------------------------------------------------------------------------
     */

    const applyBodyFont = (
        elements,
        fontSize
    ) => {
        elements.forEach((element) => {
            if (!isParagraph(element)) {
                return;
            }

            element.style.fontSize =
                `${fontSize}px`;

            element.style.lineHeight =
                "1.15";
        });
    };

    /*
     * --------------------------------------------------------------------------
     * APPLY COMPLETE HIERARCHY
     * --------------------------------------------------------------------------
     */

    const applyCompleteFont = (
        elements,
        baseFontSize
    ) => {
        elements.forEach((element) => {

            const tag = getTag(element);

            if (tag === "p") {

                element.style.fontSize =
                    `${baseFontSize}px`;

                element.style.lineHeight =
                    "1.15";

                return;
            }

            if (TITLE_SCALE[tag]) {

                element.style.fontSize =
                    `${Math.min(
                        baseFontSize * TITLE_SCALE[tag],
                        MAX_FONT_SIZE
                    )}px`;

                element.style.lineHeight =
                    "1.10";
            }
        });
    };

    /*
     * --------------------------------------------------------------------------
     * MEASURE TEXT
     * --------------------------------------------------------------------------
     */

    const getContentSize = (
        container,
        elements
    ) => {
        if (
            !container ||
            !elements.length
        ) {
            return {
                width: 0,
                height: 0,
            };
        }

        forceLayout();

        let maxWidth = 0;

        elements.forEach((element) => {
            const rect =
                element.getBoundingClientRect();

            maxWidth =
                Math.max(
                    maxWidth,
                    rect.width
                );
        });

        const height =
            Math.max(
                container.scrollHeight,
                container.getBoundingClientRect().height
            );

        return {
            width: maxWidth,
            height,
        };
    };

    /*
     * --------------------------------------------------------------------------
     * CHECK FIT
     * --------------------------------------------------------------------------
     */

    const fitsContainer = (
        container,
        elements,
        availableWidth,
        availableHeight
    ) => {
        if (
            !container ||
            !elements.length
        ) {
            return false;
        }

        forceLayout();

        const {
            width,
            height,
        } = getContentSize(
            container,
            elements
        );

        return (
            width <=
            availableWidth +
            TOLERANCE &&

            height <=
            availableHeight +
            TOLERANCE
        );
    };

    /*
     * --------------------------------------------------------------------------
     * FIND MAXIMUM FONT FOR ONE BLOCK
     * --------------------------------------------------------------------------
     */

    const findBestFontForBlock = (
        block,
        container,
        elements,
        options = {}
    ) => {
        const {
            min = MIN_FONT_SIZE,
            max = MAX_FONT_SIZE,
            apply = applyBodyFont,
        } = options;

        const {
            width,
            height,
        } = getAvailableSize(block);

        if (
            width <= 0 ||
            height <= 0
        ) {
            return min;
        }

        /*
         * First check maximum.
         */
        apply(
            elements,
            max
        );

        if (
            fitsContainer(
                container,
                elements,
                width,
                height
            )
        ) {
            return max;
        }

        /*
         * Check minimum.
         */
        apply(
            elements,
            min
        );

        if (
            !fitsContainer(
                container,
                elements,
                width,
                height
            )
        ) {
            return min;
        }

        let low = min;
        let high = max;
        let best = min;

        while (
            high - low > 0.1
        ) {
            const middle =
                (low + high) / 2;

            apply(
                elements,
                middle
            );

            if (
                fitsContainer(
                    container,
                    elements,
                    width,
                    height
                )
            ) {
                best = middle;
                low = middle;
            } else {
                high = middle;
            }
        }

        apply(
            elements,
            best
        );

        return best;
    };

    /*
     * --------------------------------------------------------------------------
     * FIND ALL TEXT BLOCKS
     * --------------------------------------------------------------------------
     */

    const blocks = Array.from(
        doc.querySelectorAll(
            ".block.text"
        )
    );

    const textBlocks = [];

    blocks.forEach((block) => {
        const container =
            getDeepestTextContainer(
                block
            );

        if (!container) {
            return;
        }

        const elements =
            getTextElements(
                container
            );

        if (!elements.length) {
            return;
        }

        elements.forEach((element) => {
            element.style.fontSize = "";
            element.style.lineHeight = "";
        });

        textBlocks.push({
            block,
            container,
            elements,

            hasParagraph:
                elements.some(
                    isParagraph
                ),

            hasTitle:
                elements.some(
                    isTitle
                ),

            special:
                isMarginalia(
                    block
                ),
        });
    });

    /*
     * --------------------------------------------------------------------------
     * STEP 1
     * --------------------------------------------------------------------------
     */

    const normalBlocks =
        textBlocks.filter((item) => {
            return (
                item.hasParagraph &&
                !item.hasTitle &&
                !item.special
            );
        });

    const individualSizes = [];

    normalBlocks.forEach((item) => {
        const best =
            findBestFontForBlock(
                item.block,
                item.container,
                item.elements
            );

        item.bestFontSize = best;

        individualSizes.push(
            best
        );
    });

    /*
     * --------------------------------------------------------------------------
     * STEP 2
     *
     * Calculate a ROBUST common font size.
     *     *
     * Example:
     *
     *  17
     *  16
     *  16
     *  15
     *  15
     *  14
     *   7   <-- tiny block
     *
     * Minimum = 7  WRONG
     *
     * Median = 15 GOOD
     *
     * --------------------------------------------------------------------------
     */

    const getMedian = (values) => {
        if (!values.length) {
            return DEFAULT_FONT_SIZE;
        }

        const sorted =
            [...values].sort(
                (a, b) => a - b
            );

        const middle =
            Math.floor(
                sorted.length / 2
            );

        if (
            sorted.length % 2 === 0
        ) {
            return (
                sorted[middle - 1] +
                sorted[middle]
            ) / 2;
        }

        return sorted[middle];
    };

    let commonBodyFont =
        getMedian(
            individualSizes
        );

    commonBodyFont =
        Math.round(
            commonBodyFont * 2
        ) / 2;

    commonBodyFont =
        Math.max(
            MIN_FONT_SIZE,
            Math.min(
                MAX_FONT_SIZE,
                commonBodyFont
            )
        );

    /*
     * --------------------------------------------------------------------------
     * STEP 3
     *
     * Apply common font to all normal blocks.
     * --------------------------------------------------------------------------
     */

    normalBlocks.forEach((item) => {
        applyBodyFont(
            item.elements,
            commonBodyFont
        );
    });

    /*
     * --------------------------------------------------------------------------
     * STEP 4
     *
     * IMPORTANT:
     *
     * Some blocks cannot fit the common font.
     *
     * Those blocks are individually reduced.
     *
     * This means:
     *
     * normal blocks ≈ same font
     *
     * but very small blocks can shrink.
     * --------------------------------------------------------------------------
     */

    normalBlocks.forEach((item) => {
        const {
            block,
            container,
            elements,
        } = item;

        const {
            width,
            height,
        } = getAvailableSize(
            block
        );

        if (
            width <= 0 ||
            height <= 0
        ) {
            return;
        }

        if (
            fitsContainer(
                container,
                elements,
                width,
                height
            )
        ) {
            return;
        }

        const best =
            findBestFontForBlock(
                block,
                container,
                elements,
                {
                    min: MIN_FONT_SIZE,
                    max: commonBodyFont,
                    apply: applyBodyFont,
                }
            );

        applyBodyFont(
            elements,
            best
        );
    });

    /*
     * --------------------------------------------------------------------------
     * STEP 5
     * --------------------------------------------------------------------------
     */

    const specialBlocks =
        textBlocks.filter(
            (item) =>
                !normalBlocks.includes(
                    item
                )
        );

    specialBlocks.forEach((item) => {
        const {
            block,
            container,
            elements,
            hasTitle,
            special,
        } = item;

        const {
            width,
            height,
        } = getAvailableSize(
            block
        );

        if (
            width <= 0 ||
            height <= 0
        ) {
            return;
        }

        /*
         * Marginalia stays smaller.
         */
        if (special) {
            const maxSize =
                Math.min(
                    commonBodyFont,
                    10
                );

            const best =
                findBestFontForBlock(
                    block,
                    container,
                    elements,
                    {
                        min:
                            MIN_FONT_SIZE,

                        max:
                            maxSize,

                        apply:
                            applyCompleteFont,
                    }
                );

            applyCompleteFont(
                elements,
                best
            );

            return;
        }

        const maxSize =
            hasTitle
                ? commonBodyFont
                : commonBodyFont;

        const best =
            findBestFontForBlock(
                block,
                container,
                elements,
                {
                    min:
                        MIN_FONT_SIZE,

                    max:
                        maxSize,

                    apply:
                        applyCompleteFont,
                }
            );

        applyCompleteFont(
            elements,
            best
        );
    });

    /*
     * --------------------------------------------------------------------------
     * STEP 6
     * --------------------------------------------------------------------------
     */

    textBlocks.forEach((item) => {
        const {
            block,
            container,
            elements,
        } = item;

        const {
            width,
            height,
        } = getAvailableSize(
            block
        );

        if (
            width <= 0 ||
            height <= 0
        ) {
            return;
        }

        /*
         * Check whether block still overflows.
         */
        if (
            fitsContainer(
                container,
                elements,
                width,
                height
            )
        ) {
            return;
        }

        let currentSize =
            Math.max(
                ...elements.map(
                    (element) => {
                        return (
                            parseFloat(
                                doc.defaultView
                                    .getComputedStyle(
                                        element
                                    )
                                    .fontSize
                            ) ||
                            commonBodyFont
                        );
                    }
                )
            );

        while (
            currentSize >
            MIN_FONT_SIZE
        ) {
            currentSize -= 0.25;

            if (item.hasTitle) {

                const titles = elements.filter(isTitle);

                titles.forEach(title => {

                    const current =
                        parseFloat(
                            doc.defaultView
                                .getComputedStyle(title)
                                .fontSize
                        );

                    title.style.fontSize =
                        `${current - 0.25}px`;
                });

            } else {

                applyBodyFont(
                    elements,
                    currentSize
                );
            }

            if (
                fitsContainer(
                    container,
                    elements,
                    width,
                    height
                )
            ) {
                break;
            }
        }
    });
        };

        const handleLoad = () => {
            const doc =
                iframe.contentDocument;

            if (!doc) return;

            const applyLayout = async () => {
                if (doc.fonts?.ready) {
                    await doc.fonts.ready;
                }

                fitTextInBlocks(doc);

                const pageElement =
                    doc.querySelector(".page");

                if (pageElement) {
                    const rect =
                        pageElement.getBoundingClientRect();

                    setContentHeight(
                        rect.height
                    );
                } else {                    
                    setContentHeight(
                        Math.max(
                            doc.body.scrollHeight,
                            doc.documentElement
                                .scrollHeight
                        )
                    );
                }
            };

            applyLayout();

            const handleMouseOver = (event) => {
                const chunk =
                    event.target.closest(
                        "[data-chunk-id]"
                    );

                if (!chunk) return;

                const chunkId =
                    chunk.getAttribute(
                        "data-chunk-id"
                    );

                if (chunkId) {
                    setHoveredChunkId(chunkId);
                }
            };

            const handleMouseLeave = () => {
                setHoveredChunkId(null);
            };

            const handleClick = (event) => {
                const chunk =
                    event.target.closest(
                        "[data-chunk-id]"
                    );

                if (!chunk) return;

                const chunkId =
                    chunk.getAttribute(
                        "data-chunk-id"
                    );

                if (!chunkId) return;

                const content =
                    editedChunksRef.current[chunkId] ??
                    extractPlainText(chunk);

                originalTextRef.current[chunkId] = content;

                const chunkRect =
                    chunk.getBoundingClientRect();

                const iframeRect = iframe.getBoundingClientRect(); 

                setAnchorRect({
                    top: iframeRect.top + chunkRect.top * scale,
                    left: iframeRect.left + chunkRect.left * scale,
                    width: chunkRect.width * scale,
                    height: chunkRect.height * scale,
                });

                if (containerRef.current) {
                    const bounds = containerRef.current.getBoundingClientRect();

                    setViewerBounds({
                        top: bounds.top,
                        left: bounds.left,
                        width: bounds.width,
                        height: bounds.height,
                    });
                }

                const meta = page?.chunks?.find(
                    (c) => c.external_id === chunkId
                );

                setSelectedBlock({
                    id: chunkId,
                    label: meta?.chunk_type || "text",
                    content,
                });
            };

            doc.addEventListener(
                "mouseover",
                handleMouseOver
            );

            doc.addEventListener(
                "mouseleave",
                handleMouseLeave
            );

            doc.addEventListener(
                "click",
                handleClick
            );

            iframe._cleanup = () => {
                doc.removeEventListener(
                    "mouseover",
                    handleMouseOver
                );

                doc.removeEventListener(
                    "mouseleave",
                    handleMouseLeave
                );

                doc.removeEventListener(
                    "click",
                    handleClick
                );
            };
        };

        iframe.addEventListener(
            "load",
            handleLoad
        );

        return () => {
            iframe.removeEventListener(
                "load",
                handleLoad
            );
        };
    }, [
        safeHtml,
        setHoveredChunkId,
        scale,
        page,
    ]);

    const updateHover = (doc, chunkId) => {

        if (
            !doc ||
            !doc.head ||
            !doc.documentElement
        ) {
            return;
        }

        let style = doc.getElementById(
            "hover-style"
        );

        if (!style) {
            style = doc.createElement("style");
            style.id = "hover-style";

            doc.head.appendChild(style);
        }

        if (!chunkId) {
            style.innerHTML = "";
            return;
        }


        const escaped = CSS.escape(chunkId);

        style.innerHTML = `
        [data-chunk-id="${escaped}"] {
            outline: 3px solid #deb9ef !important;
            background: rgba(219,197,236,0.2) !important;
        }
    `;
    };

    useEffect(() => {

        const iframe = iframeRef.current;

        if (!iframe) return;

        let cancelled = false;

        const update = () => {

            if (cancelled) return;

            const doc =
                iframe.contentDocument;

            if (!doc || !doc.head) {
                return;
            }

            updateHover(
                doc,
                hoveredChunkId
            );
        };


        update();


        return () => {
            cancelled = true;
        };

    }, [hoveredChunkId]);


    useEffect(() => {
        const iframe = iframeRef.current;

        if (!iframe) return;

        const doc = iframe.contentDocument;

        if (!doc || !doc.head) return;

        if (selectedBlock) {
            updateHover(doc, selectedBlock.id);
        }
    }, [selectedBlock]);



    const handleSwitchChange = (value) => {
        setDisplayExtractedText(value);
    };


    return (
        <div className="digitizationEditorHTMLSection" id="digitizationEditorHTMLSection"
            style={{
                display: 'flex', flexDirection: 'column', maxHeight: '100vh',
                minWidth: "20%", width: "100%"
            }}
        >
            <div className="resize-handle" onMouseDown={handleContentSectionMouseDown} />
            <Box sx={{
                borderBottom: "1px solid #e0d8d8b0",
                px: { xs: 1, sm: 2 },
                py: { xs: 0.5, sm: 1 },
                height: { xs: "auto", sm: "3.5rem" },
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: { xs: "wrap", md: "nowrap" },
                gap: { xs: 1, sm: 2 },
                minWidth: "max-content",
            }}
                 dir={dir}
            >
                <Box sx={{display: "flex", alignItems: "center", gap: 1}}>
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", gap: 2}}>
                    <ButtonGroup
                        variant="outlined"
                        disableElevation
                        sx={{
                            bgcolor: "#ffffff !important",
                            "& .MuiButton-root": {
                                height: 32,
                                fontSize: "0.75rem",
                                color: "#616161", // grey.700
                                borderColor: "#bdbdbd !important", // grey.400
                                "&:hover": {
                                    borderColor: "#757575 !important", // grey.600
                                    bgcolor: "#f5f5f5 !important", // grey.100
                                },
                                "&.Mui-focusVisible, &:focus": {
                                    outline: "none !important",
                                    boxShadow: "none !important",
                                },
                            },
                            "& .MuiButton-contained": {
                                bgcolor: "#424242 !important", // grey.800
                                color: "#ffffff",
                                "&:hover": {
                                    bgcolor: "#212121 !important",
                                    borderColor: "#bdbdbd !important",// grey.900
                                },
                            },
                            "&.Mui-focusVisible, &:focus": {
                                bgcolor: "#212121 !important",
                                borderColor: "#bdbdbd !important",
                                outline: "none !important",
                                boxShadow: "none !important",
                            },
                            "& .MuiButtonGroup-firstButton": {
                                borderTopLeftRadius: isRtl ? 0 : 4,
                                borderBottomLeftRadius: isRtl ? 0 : 4,
                                borderTopRightRadius: isRtl ? 4 : 0,
                                borderBottomRightRadius: isRtl ? 4 : 0,
                            },
                            "& .MuiButtonGroup-lastButton": {
                                borderTopRightRadius: isRtl ? 0 : 4,
                                borderBottomRightRadius: isRtl ? 0 : 4,
                                borderTopLeftRadius: isRtl ? 4 : 0,
                                borderBottomLeftRadius: isRtl ? 4 : 0,
                            },
                        }}
                    >
                        <Button
                            variant={displayExtractedText ? "contained" : "outlined"}
                            onClick={() => handleSwitchChange(true)}
                            startIcon={<FileText size={isMobile ? 10 : 12} />}
                            sx={{
                                fontSize: { xs: "0.65rem", sm: "0.75rem" },
                                px: { xs: 1, sm: 2 },
                                minWidth: { xs: 32, sm: "auto" },
                                "& .MuiButton-startIcon": {
                                    marginLeft: dir === "rtl" ? "0.3rem" : "-4px",
                                    marginRight: dir === "rtl" ? "0px" : "8px"
                                },
                            }}
                        >
                            {isMobile ? "E" : t("extracted_text")}
                        </Button>

                       {/*  {translationSelected && (
                            <Button
                                variant={!displayExtractedText ? "contained" : "outlined"}
                                onClick={() => handleSwitchChange(false)}
                                startIcon={<Languages size={isMobile ? 10 : 12} />}
                                sx={{
                                    fontSize: { xs: "0.65rem", sm: "0.75rem" },
                                    px: { xs: 1, sm: 2 },
                                    minWidth: { xs: 32, sm: "auto" },
                                    "& .MuiButton-startIcon": {
                                        marginLeft: dir === "rtl" ? "0.3rem" : "-4px",
                                        marginRight: dir === "rtl" ? "0px" : "8px"
                                    },
                                }}
                            >
                                {isMobile ? "T" : t("translated_text")}
                            </Button>
                        )} */}
                    </ButtonGroup>
                </Box>

                <React.Fragment>
                    <Box sx={{display: "flex", alignItems: "center", gap: 1 }}>
                    </Box>
                </React.Fragment>

            </Box>

            <div style={{ padding: "0px 0px 0px 15px", flexGrow: 1}}>
                {loading && (
                    <div style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(255,255,255,0.7)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 10
                    }}>
                        <CircularProgress/>
                    </div>
                )}

                <SoftBox style={{
                    direction: displayExtractedText ? getScriptDirection(sourceLanguage) : getScriptDirection(targetLanguage),
                    height: '100%'
                }}>

                    {displayExtractedText ?

                    <React.Fragment>

                    <Box
                        sx={{
                            position: "relative",
                            width: "100%",
                            height: "100%",
                            overflow: "hidden",
                        }}
                    >
                        <Box
                            ref={containerRef}
                            sx={{
                                width: "100%",
                                height: "100%",
                                overflow: "auto",
                                background: "#ffffff",
                            }}
                        >
                            <Box
                                sx={{
                                    position: "relative",

                                    width: A3_WIDTH_PX * scale,

                                    height: (contentHeight * scale) * 0.14,
                                }}
                            >
                                <iframe
                                    ref={iframeRef}
                                    title="OCR Document"
                                    srcDoc={safeHtml}
                                    sandbox="allow-same-origin"
                                    style={{
                                        position: "absolute",
                                        top: 0,
                                        left: 0,
                                        width: `${A3_WIDTH_PX}px`,
                                        height: `${contentHeight}px`,
                                        border: "none",
                                        transformOrigin:
                                            "top left",
                                        transform:
                                            `scale(${scale})`,
                                        display: "block",
                                        background:
                                            "#ffffff",
                                    }}
                                />
                            </Box>
                        </Box>
                    </Box>


                    </React.Fragment> :
                    <React.Fragment>
                    </React.Fragment>}

               </SoftBox>

                <BlockEditorModal
                    block={selectedBlock}
                    anchorRect={anchorRect}
                    boundsRect={viewerBounds}
                    onClose={handleCloseModal}
                    onSave={handleSaveBlock}
                    onLiveChange={handleLiveChange}
                />

            </div>
            {displayExtractedText && audioUrl && !loading && (
                <AudioPlayer audioUrl={audioUrl}/>
            )}
        </div>
    );
};

DigitizationHtmlBlocksComponent.propTypes = {
    containerWidth: PropTypes.string,
    pageNumber: PropTypes.number,
    digitizationId: PropTypes.number,
    configWidth: PropTypes.string,
    updateConfigWidth: PropTypes.func,
    page: PropTypes.object,
    sourceLanguage: PropTypes.string,
    targetLanguage: PropTypes.string,
    addOrUpdatePages: PropTypes.func,
    updatedPages: PropTypes.array,
    handleSave: PropTypes.func,
    translationSelected: PropTypes.bool,
    diacriticsSelected: PropTypes.bool,
    diacritizeWords: PropTypes.func,
    numPages: PropTypes.number,
    fileName: PropTypes.string,
    fetchData: PropTypes.func,
    setPage: PropTypes.func,
    setNotification: PropTypes.func,
    hoveredChunkId: PropTypes.string,
    setHoveredChunkId: PropTypes.func,
    setPdfDimensions: PropTypes.func,
};

export default DigitizationHtmlBlocksComponent;
